<script>
  import {onMount, createEventDispatcher} from 'svelte';
  import {IMAGES} from '@/const/images';
  import {PATHS} from '@/const/paths';
  import {navAfterRegisterSuccess} from '@/helper/login';
  import {mc} from '@/core';
  import {ga} from '@/da/ga';
  import {doCaptcha} from '@/captcha';
  import {userApi} from '@/api/user';
  import {fixLink$} from '@/store/lang';
  import VerifyCodeInput from '@/widgets/VerifyCodeInput.svelte';
  import errors from '@/modules/error/error.lang';
  import lang from './Verification.lang';

  export let LG = null;
  export let verificationData = null;

  const dispatch = createEventDispatcher();

  let width = 1200;
  let status = '';
  let explain = '';
  let count = 60;
  let email = '';
  let typeRef = null;
  let contextRef = null;
  let callbackRef = null;
  let signUpParams = null;
  let isSubmitted = false;
  let loginWithTokenTime = null;

  $: {
    if (isSubmitted) {
      postMailCode();
    }
  }

  onMount(() => {
    status = '';
    explain = '';
    isSubmitted = false;
    typeRef = verificationData.type;
    contextRef = verificationData.context;
    callbackRef = verificationData.callback;
    email = verificationData.context.email;
    countDown();
    if (verificationData.params) {
      signUpParams = verificationData.params;
    }
    if (verificationData.context.registerToken) {
      loginPoll(verificationData.context);
    }
  });

  function loginPoll(contextRef) {
    let countdown = 300;
    loginWithTokenTime = setInterval(async () => {
      const query = {token: contextRef.registerToken};
      const {data} = await userApi.loginWithToken(query);
      countdown = countdown - 2;
      if (data) {
        clearInterval(loginWithTokenTime);
        await ga.bindGoogleClientId();
        localStorage.setItem('base-kyc-status', '1');
        ga.event('user.welcome', 'open_welcome_page', 'after_register');
        setTimeout(() => {
          navAfterRegisterSuccess();
        }, 200);
      }
      if (countdown < 0) {
        clearInterval(loginWithTokenTime);
      }
    }, 2000);
  }

  async function postMailCode() {
    const captchaResult = await doCaptcha('safety_verification');
    const query = {
      type: typeRef,
      email: contextRef.email,
      ...captchaResult,
    };
    const signUpQuery = {...captchaResult};
    const {data, error} = await (signUpParams ? userApi.signUp(signUpQuery, signUpParams) : userApi.publicCodesMail(query));
    if (data) {
      mc.send('toast', 'info', {message: LG(lang.Verification)});
      countDown();
    }
    if (error) {
      mc.send('toast', 'error', error);
    }
    isSubmitted = false;
  }

  function countDown() {
    const nextTime = Date.now() + 60000;
    localStorage.setItem('count_down_mail', nextTime);
    count = 60;
    const timer = setInterval(() => {
      const nextTime = localStorage.getItem('count_down_mail') || '0';
      const now = Date.now();
      count = Math.floor((Number(nextTime) - now) / 1000);
      if (count < 0) {
        clearInterval(timer);
      }
    }, 500);
  }

  function handleClickResend() {
    isSubmitted = true;
  }

  function handleCodeChange(e) {
    const mailCode = e.detail.value;
    const callback = ({code, msg}) => {
      if (code === 0) {
        if (typeRef === 'mail-reset-password-code') {
          handleClickBack();
        }
        return;
      }
      status = 'fail';
      const id = errors[code];
      explain = id ? LG(id) : msg;
    };
    callbackRef && callbackRef(mailCode, callback, contextRef);
  }

  function handleInputCode() {
    status = '';
    explain = '';
  }

  function handleClickBack() {
    if (loginWithTokenTime) {
      clearInterval(loginWithTokenTime);
    }
    dispatch('close');
  }
</script>

<style>
  .warp {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    width: 100%;
    height: 100%;
    background: #003fe6;
  }
  .logo {
    margin-top: 24px;
  }
  img {
    width: 106px;
    height: 24px;
  }
  .email-gif {
    width: 100px;
    height: 100px;
  }
  .code :global(.digit-box span) {
    font-weight: var(--fw3);
    font-size: 36px;
    color: #003fe6;
  }
  @keyframes rotate {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .disable {
    color: #9fa3ab;
    pointer-events: none;
  }

  @media screen and (min-width: 640px) {
    .main {
      border-radius: 8px;
      padding: 50px 30px 40px;
      width: 340px;
      background: #fff;
    }
    .back {
      width: 400px;
    }
    .logo-a {
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      margin-left: 120px;
    }
    .content {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0 auto;
      width: 1200px;
      height: 100%;
    }
  }
  @media screen and (max-width: 640px) {
    .warp {
      background: #fff;
    }
    .back,
    .title2 {
      display: none;
    }
    .title {
      text-align: left;
      font-size: 24px;
    }
    .logo {
      margin-left: 24px;
    }
    img {
      width: 106px;
      height: 24px;
    }
    .content {
      padding: 0;
    }
    .main {
      margin: 0;
      padding: 50px 20px 40px;
    }
  }
</style>

<div class="warp" bind:offsetWidth={width}>
  <div class="content df fdc">
    <a href={$fixLink$(PATHS.HOME.pathname)} class="logo-a" on:click={() => dispatch('close')}>
      <div class="logo">
        <img alt="Logo" src={width < 640 ? IMAGES.LOGO_DARK : IMAGES.LOGO_LIGHT} />
      </div>
    </a>
    <div class="back f12 lh18 cp mb10 cf" on:click={handleClickBack}>
      <i class="iconfont arrow pa backLeft">&#xe60b;</i>
      <span class="backSpan">{LG(lang.Back)}</span>
    </div>
    <div class="main">
      <div class="title f36 lh46 fw3 T1 tc">{LG(lang.Verify)}</div>
      <div class="mt20 tc">
        <img class="email-gif" src={IMAGES.REGISTER_LANDING_PAGE_EMAIL_GIF} alt="email" />
      </div>
      <div class="title2 mt32 T4 mb24 f14 l22">
        {@html typeRef === 'login-verify' ? LG(lang.WeJustEmailedLogin, {Email: `<span class='fw3 T1'>${email}</span>`}) : LG(lang.WeJustEmailed, {Email: `<span class='fw3 T1'>${email}</span>`})}
      </div>
      <div class="code">
        <VerifyCodeInput class="home-email-code" {status} {explain} on:change={handleCodeChange} on:input={handleInputCode} />
      </div>
      <div class="mt16 T4">{LG(lang.KeepThisWindow)}</div>
      <div class="df aic jcc mt32 f14 lh18 BLUE">
        {#if count > 0}
          <div class="fw3">{count}s</div>
        {/if}
        <div class="ml10 cp" class:disable={count > 0} on:click={handleClickResend}>{LG(lang.SendAgain)}</div>
      </div>
    </div>
  </div>
</div>
